import { Switch } from '@/components/ui/switch';
import { toast } from '@/hooks/use-toast';
import { pdkAxios } from '@/lib/pdk-axios';
import { cn } from '@/lib/utils';
import type { VehicleListing } from '@/models/registry';
import { vehicleListingStore } from '@/models/stores/vehicle-listings';
import { useMutation } from '@tanstack/react-query';
import { WHOLE_SALE_OPTIONS_MAP } from './options';

export type VehicleWholesaleToggleProps = {
  id: string;
  value: VehicleListing['is_listed_wholesale'];
  className?: string;
};

export default function VehicleWholesaleToggle({
  id,
  value,
  className,
}: VehicleWholesaleToggleProps) {
  const updateWholesaleFlagMutation = useMutation<
    unknown,
    Error,
    { is_listed_wholesale: boolean },
    { previous?: boolean }
  >({
    onMutate: async ({ is_listed_wholesale }) => {
      const previous = vehicleListingStore.getById(id)?.is_listed_wholesale;
      vehicleListingStore.updateIsListedWholesale({
        id,
        is_listed_wholesale,
      });
      return { previous };
    },
    mutationFn: async ({ is_listed_wholesale }) => {
      return await pdkAxios.post(
        `/inventory/listings/${id}/is_listed_wholesale`,
        {
          is_listed_wholesale,
        },
      );
    },
    onError: (error, _variables, context) => {
      if (context?.previous !== undefined) {
        vehicleListingStore.updateIsListedWholesale({
          id,
          is_listed_wholesale: context.previous,
        });
      }

      toast({
        title: 'Error updating wholesale flag',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const wholesaleOption = WHOLE_SALE_OPTIONS_MAP.find(
    (option) => option.value === 'WHOLESALE',
  );

  const handleToggle = (is_listed_wholesale: boolean) => {
    updateWholesaleFlagMutation.mutate({ is_listed_wholesale });
  };

  return (
    <div
      className={cn(
        'flex w-full items-center justify-between gap-3 rounded-md border border-transparent py-1 transition-colors',
        value ? 'bg-primary/5 border-primary/30' : 'bg-muted/30',
        className,
      )}
    >
      <div className="flex items-center gap-2 text-xs text-gray-600">
        {wholesaleOption?.icon}
        <span>Wholesale Listing</span>
      </div>
      <Switch
        checked={Boolean(value)}
        onCheckedChange={handleToggle}
        disabled={updateWholesaleFlagMutation.isPending}
      />
    </div>
  );
}
